import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { SignatureCanvas } from '@/components/legal/SignatureCanvas';
import { BETA_AGREEMENT_CONTENT, BETA_AGREEMENT_TITLE } from '@/legal/beta_agreement';
import { Loader2, FileSignature } from 'lucide-react';

const BetaSignAgreement = () => {
  const { user, activeShop } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Form state
  const [shopName, setShopName] = useState(activeShop?.name || '');
  const [signerName, setSignerName] = useState(user?.user_metadata?.display_name || '');
  const [position, setPosition] = useState('');
  const [address, setAddress] = useState('');
  const [email, setEmail] = useState(user?.email || '');
  const [mobile, setMobile] = useState('');

  // Signature state
  const [signatureMethod, setSignatureMethod] = useState<'draw' | 'typed'>('draw');
  const [signatureImage, setSignatureImage] = useState<string | null>(null);
  const [typedSignature, setTypedSignature] = useState('');
  const [esignConsent, setEsignConsent] = useState(false);

  // ── SIGNING AND DELIVERY ARE TWO DIFFERENT OUTCOMES ──────────────────────
  // The checkbox promises both: sign electronically AND receive a copy
  // electronically. They can succeed independently, and conflating them is
  // what produced the original defect in both directions — a signer who got no
  // copy was told everything was fine, and a mail failure would otherwise read
  // as "signing failed" and invite them to sign again.
  const [signedOutcome, setSignedOutcome] = useState<{
    alreadySigned: boolean;
    copyDelivered: boolean;
    deliveredTo: string | null;
    downloadUrl: string | null;
  } | null>(null);
  const [isResending, setIsResending] = useState(false);

  const hasValidSignature = signatureMethod === 'draw' ? !!signatureImage : typedSignature.trim().length > 0;
  const canSubmit = shopName && signerName && position && email && hasValidSignature && esignConsent && !isSubmitting;

  const handleSubmit = async () => {
    if (!canSubmit || !activeShop?.id) return;
    setIsSubmitting(true);

    try {
      const { data, error } = await supabase.functions.invoke('sign-beta-agreement', {
        body: {
          shopId: activeShop.id,
          shopName,
          signerName,
          position,
          address,
          email,
          mobile,
          signatureImage: signatureMethod === 'draw' ? signatureImage : null,
          typedSignature: signatureMethod === 'typed' ? typedSignature : null,
          signatureMethod,
        },
      });

      if (error) throw error;

      // Invalidate and refetch the signing gate cache before navigating
      await queryClient.invalidateQueries({ queryKey: ['beta-signing-gate'] });
      await queryClient.refetchQueries({ queryKey: ['beta-signing-gate'] });

      const copyDelivered = data?.copy_delivered === true;
      const deliveredTo: string | null = data?.delivered_to ?? null;

      setSignedOutcome({
        alreadySigned: data?.already_signed === true,
        copyDelivered,
        deliveredTo,
        downloadUrl: data?.download_url ?? null,
      });

      // The signature succeeded. Say so FIRST and unconditionally — delivery is
      // reported as its own fact, never as a qualifier on the legal outcome.
      toast({
        title: data?.already_signed ? 'Agreement Already Signed' : 'Agreement Signed Successfully',
        description: copyDelivered
          ? `A copy has been sent to ${deliveredTo}.`
          : "We couldn't email your copy — you can download it or try again below.",
      });

      // A delivered copy means nothing is left to do here. A failed delivery
      // keeps the signer on this step with a retry and a download, rather than
      // stranding them or making them sign a second time.
      if (copyDelivered) navigate('/beta/legal-review');
    } catch (err: any) {
      console.error('Sign beta agreement error:', err);
      toast({
        title: 'Signing Failed',
        description: err.message || 'Could not sign the agreement. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  /**
   * Retry DELIVERY only. `resendCopy` makes the edge function re-send from the
   * already-stored PDF: no new agreement is generated and no second acceptance
   * row is written, so a retry cannot duplicate the legal record.
   */
  const handleResendCopy = async () => {
    if (!activeShop?.id || isResending) return;
    setIsResending(true);
    try {
      const { data, error } = await supabase.functions.invoke('sign-beta-agreement', {
        body: { shopId: activeShop.id, shopName, signerName, position, address, email, mobile, signatureMethod, resendCopy: true },
      });
      if (error) throw error;

      const copyDelivered = data?.copy_delivered === true;
      setSignedOutcome((prev) => ({
        alreadySigned: true,
        copyDelivered,
        deliveredTo: data?.delivered_to ?? prev?.deliveredTo ?? null,
        downloadUrl: data?.download_url ?? prev?.downloadUrl ?? null,
      }));
      toast({
        title: copyDelivered ? 'Copy Sent' : 'Still Unable to Email',
        description: copyDelivered
          ? `A copy has been sent to ${data?.delivered_to ?? email}.`
          : 'Your agreement is still signed. You can download it here instead.',
        variant: copyDelivered ? undefined : 'destructive',
      });
    } catch (err: any) {
      toast({
        title: 'Still Unable to Email',
        description: 'Your agreement is still signed. You can download it here instead.',
        variant: 'destructive',
      });
    } finally {
      setIsResending(false);
    }
  };

  return (
    <div className="min-h-screen bg-background p-4 md:p-8">
      <div className="max-w-3xl mx-auto space-y-6">
        <div className="text-center space-y-2">
          <FileSignature className="h-10 w-10 mx-auto text-primary" />
          <h1 className="text-2xl font-bold">{BETA_AGREEMENT_TITLE}</h1>
          <p className="text-muted-foreground">
            Please review and electronically sign the beta testing agreement below.
          </p>
        </div>

        {/* ── SIGNED, BUT THE COPY DID NOT GO OUT ────────────────────────────
            Only rendered when delivery failed; a delivered copy navigates on.
            The signature is stated as fact FIRST, so nothing here can read as
            "you are not signed". Both escape routes are offered, and neither
            of them is "sign again". */}
        {signedOutcome && !signedOutcome.copyDelivered && (
          <Card
            data-testid="agreement-copy-undelivered"
            className="border-amber-500/50 bg-amber-500/5"
          >
            <CardContent className="pt-6 space-y-4">
              <div className="space-y-1">
                <p className="font-medium">Agreement signed successfully.</p>
                <p className="text-sm text-muted-foreground">
                  We couldn&apos;t email your copy. Your signature is on file and
                  does not need to be repeated &mdash; you can download the executed
                  agreement now, or have us try sending it again.
                </p>
              </div>
              <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
                {signedOutcome.downloadUrl && (
                  <Button asChild variant="outline" className="w-full sm:w-auto">
                    <a
                      href={signedOutcome.downloadUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="download-signed-agreement"
                    >
                      Download Signed Agreement
                    </a>
                  </Button>
                )}
                <Button
                  variant="outline"
                  onClick={handleResendCopy}
                  disabled={isResending}
                  className="w-full sm:w-auto"
                  data-testid="resend-agreement-copy"
                >
                  {isResending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Email my copy again
                </Button>
                <Button
                  onClick={() => navigate('/beta/legal-review')}
                  className="w-full sm:w-auto"
                  data-testid="continue-after-signing"
                >
                  Continue
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Agreement Text */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Agreement Text</CardTitle>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-64 rounded-md border p-4">
              <pre className="whitespace-pre-wrap text-sm text-foreground font-sans leading-relaxed">
                {BETA_AGREEMENT_CONTENT}
              </pre>
            </ScrollArea>
          </CardContent>
        </Card>

        {/* Signer Information */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Signer Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="shopName">Repair Shop Name *</Label>
                <Input id="shopName" value={shopName} onChange={e => setShopName(e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="signerName">Signer Name *</Label>
                <Input id="signerName" value={signerName} onChange={e => setSignerName(e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="position">Position / Title *</Label>
                <Input id="position" value={position} onChange={e => setPosition(e.target.value)} placeholder="e.g. Owner, Manager" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email *</Label>
                <Input id="email" type="email" value={email} onChange={e => setEmail(e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="address">Address</Label>
                <Input id="address" value={address} onChange={e => setAddress(e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="mobile">Mobile</Label>
                <Input id="mobile" type="tel" value={mobile} onChange={e => setMobile(e.target.value)} />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Signature */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Signature</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <Tabs value={signatureMethod} onValueChange={v => setSignatureMethod(v as 'draw' | 'typed')}>
              <TabsList>
                <TabsTrigger value="draw">Draw Signature</TabsTrigger>
                <TabsTrigger value="typed">Type Signature</TabsTrigger>
              </TabsList>
              <TabsContent value="draw" className="mt-4">
                <SignatureCanvas onSignatureChange={setSignatureImage} />
              </TabsContent>
              <TabsContent value="typed" className="mt-4">
                <div className="space-y-2">
                  <Label htmlFor="typedSig">Type your full legal name</Label>
                  <Input
                    id="typedSig"
                    value={typedSignature}
                    onChange={e => setTypedSignature(e.target.value)}
                    className="text-xl italic font-serif"
                    placeholder="Full legal name"
                  />
                  {typedSignature && (
                    <p className="text-2xl italic font-serif text-primary mt-2 border-b border-primary/30 pb-1 inline-block">
                      {typedSignature}
                    </p>
                  )}
                </div>
              </TabsContent>
            </Tabs>

            <div className="flex items-start gap-2 pt-4">
              <Checkbox
                id="esignConsent"
                checked={esignConsent}
                onCheckedChange={v => setEsignConsent(v === true)}
              />
              <Label htmlFor="esignConsent" className="text-sm leading-relaxed cursor-pointer">
                I agree to electronically sign this agreement and receive a copy electronically.
              </Label>
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-end gap-3">
          <Button variant="outline" onClick={() => navigate(-1)} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!canSubmit}>
            {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Sign Agreement
          </Button>
        </div>
      </div>
    </div>
  );
};

export default BetaSignAgreement;
